import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MaterialModule } from '../material.module';
import { PatProvider, UserPatStatus, UsersService } from '../services/users.service';
import { MessageDialogComponent } from '../shared/message-dialog/message-dialog.component';
import { SpinnerComponent } from '../shared/spinner/spinner.component';
import { UtcDatePipe } from '../pipes/utc-date.pipe';
import { LeftMenuComponent } from '../menu/left-menu/left-menu.component';

interface PatState {
  status: UserPatStatus | null;
  isLoading: boolean;
  isSaving: boolean;
  isTesting: boolean;
  isRemoving: boolean;
  showForm: boolean;
  showPat: boolean;
  patInput: string;
  expiresAtInput: string;
  expiryPreset: '' | '30' | '60' | '90' | 'custom';
  isValidated: boolean;
  validationMessage: string | null;
  error: string | null;
}

@Component({
  selector: 'app-personal-access-tokens',
  templateUrl: './personal-access-tokens.component.html',
  styleUrls: ['./personal-access-tokens.component.css'],
  imports: [CommonModule, FormsModule, MaterialModule, SpinnerComponent, UtcDatePipe, LeftMenuComponent]
})
export class PersonalAccessTokensComponent implements OnInit {
  readonly providers: PatProvider[] = ['azure-devops', 'github'];
  readonly states: Record<PatProvider, PatState> = {
    'azure-devops': this.createState(),
    github: this.createState()
  };

  constructor(private usersService: UsersService, private dialog: MatDialog) {}

  ngOnInit(): void {
    this.providers.forEach((provider) => this.loadStatus(provider));
  }

  providerLabel(provider: PatProvider): string {
    return provider === 'github' ? 'GitHub / GitHub Enterprise' : 'Azure DevOps';
  }

  providerDescription(provider: PatProvider): string {
    return provider === 'github'
      ? 'Used to read repositories during GitHub code validation.'
      : 'Used to read repositories and create Azure DevOps work items.';
  }

  getState(provider: PatProvider): PatState {
    return this.states[provider];
  }

  statusLabel(provider: PatProvider): string {
    const state = this.getState(provider);
    if (state.isLoading) return 'Checking status';
    if (!state.status?.hasPatConfigured) return 'Not configured';
    if (this.expiryWarning(provider) === 'expired') return 'Expired';
    if (this.expiryWarning(provider) === 'expiring-soon') return 'Expiring soon';
    return 'Configured';
  }

  expiryWarning(provider: PatProvider): 'expired' | 'expiring-soon' | null {
    const expiresAt = this.getState(provider).status?.expiresAt;
    if (!expiresAt) return null;

    const expires = new Date(expiresAt).getTime();
    const now = Date.now();
    if (expires <= now) return 'expired';
    if (expires - now <= 30 * 24 * 60 * 60 * 1000) return 'expiring-soon';
    return null;
  }

  openForm(provider: PatProvider): void {
    const state = this.getState(provider);
    state.showForm = true;
    state.error = null;
    state.patInput = '';
    state.showPat = false;
    state.isValidated = false;
    state.validationMessage = null;
    state.expiresAtInput = state.status?.expiresAt
      ? new Date(state.status.expiresAt).toISOString().substring(0, 10)
      : '';
    state.expiryPreset = state.expiresAtInput ? 'custom' : '30';
    this.onExpiryPresetChange(provider);
  }

  cancelForm(provider: PatProvider): void {
    const state = this.getState(provider);
    state.showForm = false;
    state.patInput = '';
    state.showPat = false;
    state.isValidated = false;
    state.validationMessage = null;
    state.error = null;
  }

  onPatInputChange(provider: PatProvider): void {
    const state = this.getState(provider);
    state.isValidated = false;
    state.validationMessage = null;
    state.error = null;
  }

  testToken(provider: PatProvider): void {
    const state = this.getState(provider);
    if (!state.patInput.trim() || state.isTesting || state.isSaving) return;

    state.isTesting = true;
    state.isValidated = false;
    state.validationMessage = null;
    state.error = null;

    this.usersService.validateUserPat(state.patInput.trim(), provider).subscribe({
      next: (result) => {
        state.isTesting = false;
        state.isValidated = true;
        state.validationMessage = result.message;
      },
      error: (error) => {
        state.isTesting = false;
        state.error = error.error?.message || 'Could not verify the token. Please try again.';
      }
    });
  }

  testStoredToken(provider: PatProvider): void {
    const state = this.getState(provider);
    if (!state.status?.hasPatConfigured || state.isTesting || state.isRemoving) return;

    state.isTesting = true;
    state.validationMessage = null;
    state.error = null;

    this.usersService.testStoredUserPat(provider).subscribe({
      next: (result) => {
        state.isTesting = false;
        state.validationMessage = result.message;
      },
      error: (error) => {
        state.isTesting = false;
        state.error = error.error?.message || 'Could not verify the stored token. Please try again.';
      }
    });
  }

  onExpiryPresetChange(provider: PatProvider): void {
    const state = this.getState(provider);
    if (!state.expiryPreset) {
      state.expiresAtInput = '';
      return;
    }
    if (state.expiryPreset === 'custom') return;

    const days = Number(state.expiryPreset);
    if (!Number.isFinite(days) || days <= 0) return;
    const date = new Date();
    date.setUTCDate(date.getUTCDate() + days);
    state.expiresAtInput = date.toISOString().substring(0, 10);
  }

  save(provider: PatProvider): void {
    const state = this.getState(provider);
    if (!state.patInput.trim() || !state.isValidated || state.isSaving) return;

    state.isSaving = true;
    state.error = null;
    this.usersService.saveUserPat(state.patInput.trim(), this.resolveExpiresAt(state.expiresAtInput), provider).subscribe({
      next: () => {
        state.isSaving = false;
        state.showForm = false;
        state.patInput = '';
        state.showPat = false;
        state.isValidated = false;
        state.validationMessage = null;
        this.loadStatus(provider);
      },
      error: (error) => {
        state.isSaving = false;
        state.error = error.error?.message || 'Could not save the token. Please try again.';
      }
    });
  }

  remove(provider: PatProvider): void {
    const reference = this.dialog.open(MessageDialogComponent, {
      width: '460px',
      disableClose: true,
      data: {
        title: `Remove ${this.providerLabel(provider)} token?`,
        message: 'This removes the encrypted copy from this application. It does not revoke the token at the provider.',
        confirmMode: true,
        confirmLabel: 'Remove token',
        type: 'warning'
      }
    });

    reference.afterClosed().subscribe((confirmed) => {
      if (!confirmed) return;
      const state = this.getState(provider);
      state.isRemoving = true;
      state.error = null;
      this.usersService.removeUserPat(provider).subscribe({
        next: () => {
          state.isRemoving = false;
          state.showForm = false;
          state.patInput = '';
          this.loadStatus(provider);
        },
        error: (error) => {
          state.isRemoving = false;
          state.error = error.error?.message || 'Could not remove the token. Please try again.';
        }
      });
    });
  }

  private loadStatus(provider: PatProvider): void {
    const state = this.getState(provider);
    state.isLoading = true;
    this.usersService.getPatStatus(provider).subscribe({
      next: (status) => {
        state.status = status;
        state.isLoading = false;
      },
      error: (error) => {
        state.isLoading = false;
        state.error = error.error?.message || 'Could not load token status. Please refresh the page.';
      }
    });
  }

  private resolveExpiresAt(date: string): string | null {
    return date ? new Date(date).toISOString() : null;
  }

  private createState(): PatState {
    return {
      status: null,
      isLoading: false,
      isSaving: false,
      isTesting: false,
      isRemoving: false,
      showForm: false,
      showPat: false,
      patInput: '',
      expiresAtInput: '',
      expiryPreset: '30',
      isValidated: false,
      validationMessage: null,
      error: null
    };
  }
}
